import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { sanitizeSvg } from '../../utils/sanitize';
import { ratioSvg, svgDeDataUrl, avecViewBox, styleAjuste } from './zoomFit';

/**
 * Composant réutilisable pour afficher un média (SVG ou image) avec zoom au clic.
 * Au clic → modal plein écran : le média prend la plus grande taille qui tient
 * dans l'écran, en largeur ET en hauteur, sans ascenseur (prof 2026-09-25 ;
 * voir zoomFit.js). Fixe ou mobile : la boîte suit le viewport.
 *
 * @param {'svg'|'image'} type - Le type de média
 * @param {string} content - Le contenu SVG brut (si type='svg')
 * @param {string} src - L'URL de l'image (si type='image')
 * @param {string} alt - Le texte alternatif (si type='image')
 * @param {string} className - Classes Tailwind pour le container compact
 * @param {string} imgClassName - Classes Tailwind pour l'image en mode compact (défaut: max-h contraints)
 */
const ZoomableMedia = memo(function ZoomableMedia({ type = 'svg', content, src, alt = '', className = '', imgClassName, onLoad, onError, svgThemeStyle }) {
    const [isZoomed, setIsZoomed] = useState(false);
    // Rapport largeur / hauteur d'une image raster, connu au chargement (les SVG se lisent dans le texte).
    const [ratioNaturel, setRatioNaturel] = useState(null);

    // Pas de défilement de la page derrière l'agrandi (comme ChoixZoomButton).
    useEffect(() => {
        if (!isZoomed) return undefined;
        const avant = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = avant; };
    }, [isZoomed]);

    // Guard : rien à afficher
    if (type === 'svg' && !content) return null;
    if (type === 'image' && !src) return null;

    const sanitized = type === 'svg' ? sanitizeSvg(content) : null;
    const svgAgrandi = type === 'svg' ? avecViewBox(sanitized) : null;
    const svgDuSrc = type === 'image' ? svgDeDataUrl(src) : null;
    const ratio = type === 'svg' ? ratioSvg(sanitized) : (svgDuSrc ? ratioSvg(svgDuSrc) : ratioNaturel);

    // Les SVG data URL (générateurs) ont des couleurs claires pour fond sombre → garder fond sombre au zoom
    const isSvgDataUrl = type === 'image' && src?.startsWith('data:image/svg');
    // Si svgThemeStyle est fourni, le SVG s'adapte au thème → fond neutre. Sinon, fond sombre par défaut.
    const zoomBg = svgThemeStyle
        ? (svgThemeStyle['--svg-bg'] === '#ffffff' ? 'bg-white border border-gray-200' : 'bg-slate-800 border border-slate-600')
        : (type === 'svg' || isSvgDataUrl) ? 'bg-slate-800 border border-slate-600' : 'bg-white';

    return (
        <>
            {/* Média compact — cliquable */}
            <div
                className={`cursor-pointer transition-transform hover:scale-[1.02] ${className}`}
                onClick={() => setIsZoomed(true)}
                title="Cliquer pour agrandir"
                style={type === 'svg' && svgThemeStyle ? svgThemeStyle : undefined}
            >
                {type === 'svg' ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitized }} />
                ) : (
                    <img
                        src={src}
                        alt={alt}
                        className={imgClassName || "max-h-[200px] sm:max-h-[250px] object-contain rounded-lg"}
                        onLoad={(e) => {
                            const im = e.currentTarget;
                            if (im.naturalWidth && im.naturalHeight) setRatioNaturel(im.naturalWidth / im.naturalHeight);
                            if (onLoad) onLoad(e);
                        }}
                        onError={onError}
                    />
                )}
            </div>

            {/* Modal plein écran au clic : la boîte prend la plus grande taille qui tient dans l'écran, jamais d'ascenseur */}
            {isZoomed && createPortal(
                <div
                    className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 cursor-pointer overflow-hidden"
                    onClick={() => setIsZoomed(false)}
                    style={{ animation: 'fadeIn 0.2s ease-out' }}
                >
                    {/* Bouton fermer */}
                    <button
                        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xl transition-colors"
                        onClick={() => setIsZoomed(false)}
                        aria-label="Fermer"
                    >
                        ✕
                    </button>

                    {/* Contenu en grand */}
                    <div
                        className={`${zoomBg} rounded-2xl p-2 sm:p-3 overflow-hidden shadow-2xl flex items-center justify-center`}
                        onClick={e => e.stopPropagation()}
                        style={{ ...styleAjuste(ratio), ...(type === 'svg' && svgThemeStyle ? svgThemeStyle : {}) }}
                    >
                        {type === 'svg' ? (
                            <div
                                className="w-full h-full [&_svg]:block [&_svg]:w-full [&_svg]:h-full"
                                dangerouslySetInnerHTML={{ __html: svgAgrandi }}
                            />
                        ) : (
                            <img src={src} alt={alt} className={ratio ? 'w-full h-full object-contain' : 'max-w-full max-h-[80vh] object-contain'} />
                        )}
                    </div>

                    {/* Hint */}
                    <p className="absolute bottom-2 sm:bottom-4 text-white/40 text-xs">Cliquer en dehors pour fermer</p>
                </div>,
                document.body
            )}
        </>
    );
});

export default ZoomableMedia;
